import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useLocation, useNavigate, useParams, Navigate,
} from 'react-router';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import LimitedPageContainer from '../../../components/limited-page-block/limited-page-container.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import Loader from '../../../components/loader/loader.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import DealTemplateSummary from './deal-template-summary/deal-template-summary.component';
import DealTemplateCapitalization from './deal-template-capitalization/deal-template-capitalization.component';
import DealTemplateProForma from './deal-template-pro-forma/deal-template-pro-forma.component';
import DealTemplateDueDiligence from './deal-template-due-diligence/deal-template-due-diligence.component';
import DealTemplateDealTerms from './deal-template-deal-terms/deal-template-deal-terms.component';
import DealTemplateDealFiles from './deal-template-deal-files/deal-template-deal-files.component';
import DealTemplateSchedule from './deal-template-schedule/deal-template-schedule.component';
import DealTemplateParticipants from './deal-template-participants/deal-template-participants.component';
import DealTemplateDealActivity from './deal-template-deal-activity/deal-template-deal-activity.component';
import DealTemplateDealListTypes from './deal-template-deal-list-types/deal-template-deal-list-types.component';
import SettingsDealTemplateValidator from './settings-deal-template.validator';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useEditModeQuery from '../../../hooks/use-edit-mode-query';
import useFormBlock from '../../../hooks/use-form-block';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import { getDealTemplateData, saveDealTemplateData, resetDealTemplateData } from './settings-deal-template.action';
import { toggleEditMode } from '../../app/controls/controls.action';
import { resetFormErrors } from '../../app/app.action';
import { DEAL_TEMPLATE_EDIT } from '../../../constants/entity/permissions';
import { FORM_ID_SETTINGS_DEAL_TEMPLATE } from '../../../constants/system/form-ids';
import { ROUTE_DEAL_TEMPLATE, ROUTE_NOT_FOUND, ROUTE_PARAM_ID } from '../../../constants/system/routes';
import {
  DEAL_TEMPLATE_TYPE_DUE_DILIGENCE_TEMPLATE, DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
} from '../../../constants/entity/deal-template-types';
import {
  TAB_NAME_DEAL_TEMPLATES, TAB_NAME_SUMMARY, TAB_NAME_CAPITALIZATION, TAB_NAME_PRO_FORMA, TAB_NAME_DUE_DILIGENCE,
  TAB_NAME_DEAL_TERMS, TAB_NAME_DEAL_FILES, TAB_NAME_SCHEDULE, TAB_NAME_PARTICIPANTS, TAB_NAME_DEAL_ACTIVITY,
  TAB_NAME_DEAL_LIST_TYPES,
} from '../../../constants/system/tab-names';
import { REPORT_DEAL_CUSTOM_SECTION_FIELD_MAP } from '../../../constants/entity/report-type-lists';
import { REPORT_TYPE_DEAL_BUILD_TO_SUIT_PIPELINE_SUMMARY } from '../../../constants/entity/report-types';
import { TAB_INDEX_DEFAULT } from '../../../constants/system/tab-indexes';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { DEAL_TEMPLATE_KEY_LIST } from '../../../constants/entity/deal-template-keys';
import { FIELD_TYPE_DROPDOWN, FIELD_TYPE_TEXT } from '../../../constants/entity/field-types';

const SettingsDealTemplate: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    dealTemplateData,
    dealTemplateTypeList,
    portfolioList,
    measureUnitList,
    fieldTypeList,
    fieldListTypeList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const location: ReactRouterLocation = useLocation();
  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const dealTemplateId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const dealTemplateType: string = dealTemplateData ? dealTemplateData.type : undefined;
  const dealTemplateTitle: string = dealTemplateData ? dealTemplateData.title : undefined;
  const dealTemplateInitialDataDefault: DealTemplateInitialData | undefined = dealTemplateData
    ? dealTemplateData.initialData : undefined;
  const dealTemplateHiddenFieldsDefault: string[] = (dealTemplateData && dealTemplateData.hiddenFields) || [];
  const dealTemplateCustomSectionsDefault: DealTemplateSection[] = (dealTemplateData
    && dealTemplateData.customSections) || [];
  const dealTemplateCustomListTypesDefault: FieldListType[] = (dealTemplateData
    && dealTemplateData.customListTypes) || [];
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isFormProcessed, setIsFormProcessed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [dealTemplateInitialData, setDealTemplateInitialData]: [DealTemplateInitialData | null, Function]
    = useState(null);
  const [dealTemplateHiddenFields, setDealTemplateHiddenFields]: [string[], Function] = useState([]);
  const [dealTemplateCustomSections, setDealTemplateCustomSections]: [DealTemplateSection[], Function] = useState([]);
  const [dealTemplateCustomListTypes, setDealTemplateCustomListTypes]: [FieldListType[], Function] = useState([]);
  const [isDealTemplateChanged, setIsDealTemplateChanged]: [boolean, Function] = useState(false);

  const pageTitle: string = `Deal Template${dealTemplateTitle ? `: ${dealTemplateTitle}` : ''}`;
  const isFormDataPristine: boolean = isFormPristine && !isDealTemplateChanged;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));

  const commonFieldListTypeList: FieldListType[] = useMemo((): FieldListType[] => [
    ...(fieldListTypeList || []),
    ...(dealTemplateCustomListTypesDefault || []),
  ], [fieldListTypeList, dealTemplateCustomListTypesDefault]);

  const dealTemplateEditPermissions: string[] = useMemo((): string[] => [DEAL_TEMPLATE_EDIT], []);

  const resetDynamicData: Function = useCallback((): void => {
    setDealTemplateInitialData(dealTemplateInitialDataDefault);
    setDealTemplateHiddenFields(dealTemplateHiddenFieldsDefault);
    setDealTemplateCustomSections(dealTemplateCustomSectionsDefault);
    setDealTemplateCustomListTypes(dealTemplateCustomListTypesDefault);
  }, [
    dealTemplateInitialDataDefault, dealTemplateHiddenFieldsDefault, dealTemplateCustomSectionsDefault,
    dealTemplateCustomListTypesDefault,
  ]);

  const onCancelForm: Function = useCallback((): void => {
    resetDynamicData();

    setIsDealTemplateChanged(false);

    FormService.forceResetForm(resetForm, formProps);

    dispatch(toggleEditMode(false));
  }, [
    dealTemplateInitialDataDefault, dealTemplateHiddenFieldsDefault, dealTemplateCustomSectionsDefault,
    dealTemplateCustomListTypesDefault,
  ]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, [
    dealTemplateInitialDataDefault, dealTemplateHiddenFieldsDefault, dealTemplateCustomSectionsDefault,
    dealTemplateCustomListTypesDefault,
  ]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onEditModeButtonClick: Function = useCallback((): void => {
    dispatch(toggleEditMode(!isEditMode));
  }, [isEditMode]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealTemplateItem: DealTemplateInput = {
      ...values,
    };

    if (!dealTemplateItem.description) {
      dealTemplateItem.description = null;
    }

    if (dealTemplateItem.initialData) {
      const initialData: DealTemplateInitialDataInput = { ...dealTemplateItem.initialData };

      const dealBuildingDetailsAssetUseBreakdownHeaders: Record<string, FormValueItem> = {
        ...initialData.dealBuildingDetailsAssetUseBreakdownHeaders as Record<string, FormValueItem>,
      };

      const resultDealBuildingDetailsAssetUseBreakdownHeaders: DealAssetUseBreakdownHeaderInput[] = [];
      if (dealBuildingDetailsAssetUseBreakdownHeaders
        && Object.keys(dealBuildingDetailsAssetUseBreakdownHeaders).length) {
        Object.keys(dealBuildingDetailsAssetUseBreakdownHeaders).forEach((
          dealAssetUseBreakdownHeaderIndex: string,
        ): void => {
          const dealAssetUseBreakdownHeader: FormValueItem = {
            ...dealBuildingDetailsAssetUseBreakdownHeaders[dealAssetUseBreakdownHeaderIndex],
          };

          if (dealAssetUseBreakdownHeader && dealAssetUseBreakdownHeader.name) {
            resultDealBuildingDetailsAssetUseBreakdownHeaders.push({
              id: dealAssetUseBreakdownHeader.id,
              name: dealAssetUseBreakdownHeader.name,
            });
          }
        });
      }
      initialData.dealBuildingDetailsAssetUseBreakdownHeaders = resultDealBuildingDetailsAssetUseBreakdownHeaders;

      const dealContacts: Record<string, FormValueItem> = {
        ...initialData.dealContacts as Record<string, FormValueItem>,
      };

      const resultDealContacts: DealContactGroupInput[] = [];
      if (dealContacts && Object.keys(dealContacts).length) {
        Object.keys(dealContacts).forEach((dealContactsGroupIndex: string): void => {
          const dealContactsGroupItem: FormValueItem = { ...dealContacts[dealContactsGroupIndex] };

          if (dealContactsGroupItem && dealContactsGroupItem.name) {
            resultDealContacts.push({
              id: dealContactsGroupItem.id,
              name: dealContactsGroupItem.name,
              contacts: [],
            });
          }
        });
      }
      initialData.dealContacts = resultDealContacts;

      const dealInvestmentReturns: Record<string, FormValueItem> = {
        ...initialData.dealInvestmentReturns as Record<string, FormValueItem>,
      };

      const resultDealInvestmentReturns: DealInvestmentReturnsItemInput[] = [];
      if (dealInvestmentReturns && Object.keys(dealInvestmentReturns).length) {
        Object.keys(dealInvestmentReturns).forEach((dealInvestmentReturnsIndex: string): void => {
          const dealInvestmentReturnsItem: FormValueItem = { ...dealInvestmentReturns[dealInvestmentReturnsIndex] };

          if (dealInvestmentReturnsItem && dealInvestmentReturnsItem.name) {
            resultDealInvestmentReturns.push({
              id: dealInvestmentReturnsItem.id,
              name: dealInvestmentReturnsItem.name,
            });
          }
        });
      }
      initialData.dealInvestmentReturns = resultDealInvestmentReturns;

      const dealCapitalization: FormValueItem = {
        ...initialData.dealCapitalization as FormValueItem,
      };

      const resultDealCapitalizationSourceGroups: DealCapitalizationGroupInput[] = [];
      const resultDealCapitalizationUseGroups: DealCapitalizationGroupInput[] = [];
      if (dealCapitalization) {
        if (dealCapitalization.sources) {
          Object.keys(dealCapitalization.sources).forEach((dealCapitalizationGroupIndex: string): void => {
            const dealCapitalizationGroup: FormValueItem = {
              ...dealCapitalization.sources[dealCapitalizationGroupIndex],
            };

            if (dealCapitalizationGroup && dealCapitalizationGroup.name) {
              resultDealCapitalizationSourceGroups.push({
                id: dealCapitalizationGroup.id,
                name: dealCapitalizationGroup.name,
                items: [],
              });
            }
          });
        }

        if (dealCapitalization.uses) {
          Object.keys(dealCapitalization.uses).forEach((dealCapitalizationGroupIndex: string): void => {
            const dealCapitalizationGroup: FormValueItem = {
              ...dealCapitalization.uses[dealCapitalizationGroupIndex],
            };

            if (dealCapitalizationGroup && dealCapitalizationGroup.name) {
              resultDealCapitalizationUseGroups.push({
                id: dealCapitalizationGroup.id,
                name: dealCapitalizationGroup.name,
                items: [],
              });
            }
          });
        }
      }
      initialData.dealCapitalization = {
        sources: resultDealCapitalizationSourceGroups,
        uses: resultDealCapitalizationUseGroups,
      };

      const dealTermGroups: Record<string, FormValueItem> = {
        ...initialData.dealTermGroups as Record<string, FormValueItem>,
      };

      const resultDealTermGroups: DealTermGroupInput[] = [];
      if (dealTermGroups && Object.keys(dealTermGroups).length) {
        Object.keys(dealTermGroups).forEach((dealTermGroupIndex: string): void => {
          const dealTermGroup: FormValueItem = { ...dealTermGroups[dealTermGroupIndex] };

          if (dealTermGroup && dealTermGroup.name) {
            resultDealTermGroups.push({
              id: dealTermGroup.id,
              name: dealTermGroup.name,
              terms: [],
            });
          }
        });
      }
      initialData.dealTermGroups = resultDealTermGroups;

      const dealValuationFileGroups: Record<string, FormValueItem> = {
        ...initialData.dealValuationFileGroups as Record<string, FormValueItem>,
      };

      const resultDealValuationFileGroups: DealFileGroupInput[] = [];
      if (dealValuationFileGroups && Object.keys(dealValuationFileGroups).length) {
        Object.keys(dealValuationFileGroups).forEach((dealFileGroupIndex: string): void => {
          const dealFileGroup: FormValueItem = { ...dealValuationFileGroups[dealFileGroupIndex] };

          if (dealFileGroup && dealFileGroup.name) {
            resultDealValuationFileGroups.push({
              id: dealFileGroup.id,
              name: dealFileGroup.name,
            });
          }
        });
      }
      initialData.dealValuationFileGroups = resultDealValuationFileGroups;

      const dealTaskGroups: Record<string, FormValueItem> = {
        ...initialData.dealTaskGroups as Record<string, FormValueItem>,
      };

      const resultDealTaskGroups: DealTaskGroupInput[] = [];
      if (dealTaskGroups && Object.keys(dealTaskGroups).length) {
        Object.keys(dealTaskGroups).forEach((dealTaskGroupIndex: string): void => {
          const dealTaskGroup: FormValueItem = { ...dealTaskGroups[dealTaskGroupIndex] };

          if (dealTaskGroup && dealTaskGroup.name) {
            const resultDealTasks: DealTaskInput[] = [];

            if (dealTaskGroup.tasks && Object.keys(dealTaskGroup.tasks).length) {
              Object.keys(dealTaskGroup.tasks).forEach((dealTaskIndex: string): void => {
                const dealTask: FormValueItem = { ...dealTaskGroup.tasks[dealTaskIndex] };

                if (dealTask && dealTask.name) {
                  const resultDealSubTasks: DealTaskInput[] = [];

                  if (dealTask.subTasks && Object.keys(dealTask.subTasks).length) {
                    Object.keys(dealTask.subTasks).forEach((dealSubTaskIndex: string): void => {
                      const dealSubTask: FormValueItem = { ...dealTask.subTasks[dealSubTaskIndex] };

                      if (dealSubTask && dealSubTask.name) {
                        resultDealSubTasks.push({
                          id: dealSubTask.id,
                          name: dealSubTask.name,
                        });
                      }
                    });
                  }

                  resultDealTasks.push({
                    id: dealTask.id,
                    name: dealTask.name,
                    subTasks: resultDealSubTasks,
                  });
                }
              });
            }

            resultDealTaskGroups.push({
              id: dealTaskGroup.id,
              name: dealTaskGroup.name,
              tasks: resultDealTasks,
            });
          }
        });
      }
      initialData.dealTaskGroups = resultDealTaskGroups;

      const dealFileGroups: Record<string, FormValueItem> = {
        ...initialData.dealFileGroups as Record<string, FormValueItem>,
      };

      const resultDealFileGroups: DealFileGroupInput[] = [];
      if (dealFileGroups && Object.keys(dealFileGroups).length) {
        Object.keys(dealFileGroups).forEach((dealFileGroupIndex: string): void => {
          const dealFileGroup: FormValueItem = { ...dealFileGroups[dealFileGroupIndex] };

          if (dealFileGroup && dealFileGroup.name) {
            resultDealFileGroups.push({
              id: dealFileGroup.id,
              name: dealFileGroup.name,
            });
          }
        });
      }
      initialData.dealFileGroups = resultDealFileGroups;

      const dealParticipantGroups: Record<string, FormValueItem> = {
        ...initialData.dealParticipantGroups as Record<string, FormValueItem>,
      };

      const resultDealParticipantGroups: DealParticipantGroupInput[] = [];
      if (dealParticipantGroups && Object.keys(dealParticipantGroups).length) {
        Object.keys(dealParticipantGroups).forEach((dealParticipantGroupIndex: string): void => {
          const dealParticipantGroup: FormValueItem = { ...dealParticipantGroups[dealParticipantGroupIndex] };

          if (dealParticipantGroup && dealParticipantGroup.name) {
            resultDealParticipantGroups.push({
              id: dealParticipantGroup.id,
              name: dealParticipantGroup.name,
            });
          }
        });
      }
      initialData.dealParticipantGroups = resultDealParticipantGroups;

      dealTemplateItem.initialData = initialData;
    }

    if (dealTemplateItem.type === DEAL_TEMPLATE_TYPE_DUE_DILIGENCE_TEMPLATE) {
      dealTemplateItem.hiddenFields = undefined;
      dealTemplateItem.customSections = undefined;
      dealTemplateItem.customListTypes = undefined;
    } else {
      const hiddenFields: Record<string, FormValueItem> = {
        ...dealTemplateItem.hiddenFields as Record<string, FormValueItem>,
      };

      const resultHiddenFields: string[] = [];
      if (hiddenFields && Object.keys(hiddenFields).length) {
        Object.keys(hiddenFields).forEach((dealTemplateHiddenFieldName: string): void => {
          const dealTemplateHiddenField: FormValueItem = { ...hiddenFields[dealTemplateHiddenFieldName] };

          if (dealTemplateHiddenField && dealTemplateHiddenField.isVisible !== true) {
            resultHiddenFields.push(dealTemplateHiddenFieldName);
          }
        });
      }
      dealTemplateItem.hiddenFields = resultHiddenFields;

      const resultCustomSections: DealTemplateSection[] = [];
      const customSections: Record<string, FormValueItem> = {
        ...dealTemplateItem.customSections as Record<string, FormValueItem>,
      };

      if (customSections && Object.keys(customSections).length) {
        Object.keys(customSections).forEach((dealTemplateCustomSectionId: string): void => {
          const dealTemplateCustomSectionItem: FormValueItem = { ...customSections[dealTemplateCustomSectionId] };

          if (dealTemplateCustomSectionItem && Object.keys(dealTemplateCustomSectionItem).length) {
            const dealTemplateCustomSectionFields: DealTemplateSectionField[] = [];

            if (dealTemplateCustomSectionItem.fields && Object.keys(dealTemplateCustomSectionItem.fields).length) {
              Object.keys(dealTemplateCustomSectionItem.fields).forEach((
                dealTemplateCustomSectionFieldId: string,
              ): void => {
                const dealTemplateCustomSectionFieldItem: FormValueItem = {
                  ...customSections[dealTemplateCustomSectionId].fields[dealTemplateCustomSectionFieldId],
                };

                if (dealTemplateCustomSectionFieldItem && Object.keys(dealTemplateCustomSectionFieldItem).length) {
                  const resultDealTemplateCustomSectionFieldData: DealTemplateSectionField = {
                    id: dealTemplateCustomSectionFieldItem.id,
                    name: dealTemplateCustomSectionFieldItem.name,
                    type: dealTemplateCustomSectionFieldItem.type || FIELD_TYPE_TEXT,
                  };

                  if (dealTemplateCustomSectionFieldItem.type === FIELD_TYPE_DROPDOWN) {
                    if (dealTemplateCustomSectionFieldItem.listType) {
                      resultDealTemplateCustomSectionFieldData.listType = dealTemplateCustomSectionFieldItem.listType;
                    } else {
                      resultDealTemplateCustomSectionFieldData.type = FIELD_TYPE_TEXT;
                    }
                  }

                  dealTemplateCustomSectionFields.push(resultDealTemplateCustomSectionFieldData);
                }
              });
            }

            resultCustomSections.push({
              id: dealTemplateCustomSectionItem.id,
              name: dealTemplateCustomSectionItem.name,
              fields: dealTemplateCustomSectionFields,
            });
          }
        });
      }
      dealTemplateItem.customSections = resultCustomSections;

      const resultCustomListTypes: FieldListType[] = [];
      const customListTypes: Record<string, FormValueItem> = {
        ...dealTemplateItem.customListTypes as Record<string, FormValueItem>,
      };

      if (customListTypes && Object.keys(customListTypes).length) {
        Object.keys(customListTypes).forEach((dealTemplateCustomListTypeId: string): void => {
          const dealTemplateCustomListTypeItem: FormValueItem = { ...customListTypes[dealTemplateCustomListTypeId] };

          if (dealTemplateCustomListTypeItem && Object.keys(dealTemplateCustomListTypeItem).length) {
            const dealTemplateCustomListTypeOptions: FieldListTypeOption[] = [];

            if (dealTemplateCustomListTypeItem.options && Object.keys(dealTemplateCustomListTypeItem.options).length) {
              Object.keys(dealTemplateCustomListTypeItem.options).forEach((
                dealTemplateCustomListTypeOptionId: string,
              ): void => {
                const dealTemplateCustomListTypeOptionItem: FieldListTypeOption = {
                  ...customListTypes[dealTemplateCustomListTypeId].options[dealTemplateCustomListTypeOptionId],
                };

                if (dealTemplateCustomListTypeOptionItem && Object.keys(dealTemplateCustomListTypeOptionItem).length) {
                  dealTemplateCustomListTypeOptions.push({
                    id: dealTemplateCustomListTypeOptionItem.id,
                    name: dealTemplateCustomListTypeOptionItem.name,
                    title: dealTemplateCustomListTypeOptionItem.title,
                  });
                }
              });
            }

            resultCustomListTypes.push({
              id: dealTemplateCustomListTypeItem.id,
              name: dealTemplateCustomListTypeItem.name,
              title: dealTemplateCustomListTypeItem.title,
              options: dealTemplateCustomListTypeOptions,
            });
          }
        });
      }
      dealTemplateItem.customListTypes = resultCustomListTypes;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealTemplateData({
        dealTemplate: dealTemplateItem,
      }, (response: ActionResponse): void => {
        setIsFormProcessed(true);

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, withFormErrors, formValues]);

  const onRefresh: Function = useCallback((): void => {
    if (dealTemplateId) {
      dispatch(getDealTemplateData({
        dealTemplateId,
      }));
    }
  }, [dealTemplateId]);

  const onDealAssetUseBreakdownHeaderAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders) {
      const newDealAssetUseBreakdownHeaderId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders,
      );
      const formKeyDealAssetUseBreakdownHeader: string = FormService.getIndexedFormFieldKey(
        newDealAssetUseBreakdownHeaderId,
      );

      newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders = [
        ...newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders,
        {
          id: newDealAssetUseBreakdownHeaderId,
          name: null,
        },
      ];

      changeForm(`initialData.dealBuildingDetailsAssetUseBreakdownHeaders[${formKeyDealAssetUseBreakdownHeader}]`, {
        id: newDealAssetUseBreakdownHeaderId,
        name: null,
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealAssetUseBreakdownHeaderDelete: Function = useCallback((
    dealAssetUseBreakdownHeaderId: number,
  ): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders) {
      const formKeyDealAssetUseBreakdownIndex: string = FormService.getIndexedFormFieldKey(
        dealAssetUseBreakdownHeaderId,
      );

      newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders, dealAssetUseBreakdownHeaderId,
      );
      newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealBuildingDetailsAssetUseBreakdownHeaders,
      );

      changeForm(
        `initialData.dealBuildingDetailsAssetUseBreakdownHeaders[${formKeyDealAssetUseBreakdownIndex}]`,
        null,
      );

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealInvestmentReturnsItemAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealInvestmentReturns) {
      const newDealInvestmentReturnsItemId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealInvestmentReturns,
      );
      const formKeyDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
        newDealInvestmentReturnsItemId,
      );

      newDealTemplateInitialData.dealInvestmentReturns = [
        ...newDealTemplateInitialData.dealInvestmentReturns,
        {
          id: newDealInvestmentReturnsItemId,
          name: null,
        },
      ];

      changeForm(
        `initialData.dealInvestmentReturns[${formKeyDealInvestmentReturnsItem}]`,
        {
          id: newDealInvestmentReturnsItemId,
          name: null,
        },
      );

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealInvestmentReturnsItemDelete: Function = useCallback((dealInvestmentReturnsItemId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealInvestmentReturns) {
      const formKeyDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
        dealInvestmentReturnsItemId,
      );

      newDealTemplateInitialData.dealInvestmentReturns = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealInvestmentReturns, dealInvestmentReturnsItemId,
      );
      newDealTemplateInitialData.dealInvestmentReturns = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealInvestmentReturns,
      );

      changeForm(`initialData.dealInvestmentReturns[${formKeyDealInvestmentReturnsItem}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealContactsGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealContacts) {
      const newDealContactsGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealContacts,
      );
      const formKeyDealContactsGroup: string = FormService.getIndexedFormFieldKey(
        newDealContactsGroupId,
      );

      newDealTemplateInitialData.dealContacts = [
        ...newDealTemplateInitialData.dealContacts,
        {
          id: newDealContactsGroupId,
          name: null,
          contacts: [],
        },
      ];

      changeForm(`initialData.dealContacts[${formKeyDealContactsGroup}]`, {
        id: newDealContactsGroupId,
        name: null,
        contacts: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealContactsGroupDelete: Function = useCallback((dealContactsGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealContacts) {
      const formKeyDealContactsGroup: string = FormService.getIndexedFormFieldKey(
        dealContactsGroupId,
      );

      newDealTemplateInitialData.dealContacts = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealContacts, dealContactsGroupId,
      );
      newDealTemplateInitialData.dealContacts = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealContacts,
      );

      changeForm(`initialData.dealContacts[${formKeyDealContactsGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealCapitalizationSourceGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealCapitalization) {
      const newDealCapitalizationSourceGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealCapitalization.sources,
      );
      const formKeyDealCapitalizationSourceGroup: string = FormService.getIndexedFormFieldKey(
        newDealCapitalizationSourceGroupId,
      );

      newDealTemplateInitialData.dealCapitalization.sources = [
        ...newDealTemplateInitialData.dealCapitalization.sources,
        {
          id: newDealCapitalizationSourceGroupId,
          name: null,
          items: [],
        },
      ];

      changeForm(`initialData.dealCapitalization.sources[${formKeyDealCapitalizationSourceGroup}]`, {
        id: newDealCapitalizationSourceGroupId,
        name: null,
        items: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealCapitalizationSourceGroupDelete: Function = useCallback((dealCapitalizationGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealCapitalization) {
      const formKeyDealCapitalizationSourceGroup: string = FormService.getIndexedFormFieldKey(
        dealCapitalizationGroupId,
      );

      newDealTemplateInitialData.dealCapitalization.sources = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealCapitalization.sources, dealCapitalizationGroupId,
      );
      newDealTemplateInitialData.dealCapitalization.sources = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealCapitalization.sources,
      );

      changeForm(`initialData.dealCapitalization.sources[${formKeyDealCapitalizationSourceGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealCapitalizationUseGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealCapitalization) {
      const newDealCapitalizationUseGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealCapitalization.uses,
      );
      const formKeyDealCapitalizationUseGroup: string = FormService.getIndexedFormFieldKey(
        newDealCapitalizationUseGroupId,
      );

      newDealTemplateInitialData.dealCapitalization.uses = [
        ...newDealTemplateInitialData.dealCapitalization.uses,
        {
          id: newDealCapitalizationUseGroupId,
          name: null,
          items: [],
        },
      ];

      changeForm(`initialData.dealCapitalization.uses[${formKeyDealCapitalizationUseGroup}]`, {
        id: newDealCapitalizationUseGroupId,
        name: null,
        items: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealCapitalizationUseGroupDelete: Function = useCallback((dealCapitalizationGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealCapitalization) {
      const formKeyDealCapitalizationUseGroup: string = FormService.getIndexedFormFieldKey(
        dealCapitalizationGroupId,
      );

      newDealTemplateInitialData.dealCapitalization.uses = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealCapitalization.uses, dealCapitalizationGroupId,
      );
      newDealTemplateInitialData.dealCapitalization.uses = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealCapitalization.uses,
      );

      changeForm(`initialData.dealCapitalization.uses[${formKeyDealCapitalizationUseGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealFileGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealFileGroups) {
      const newDealFileGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealFileGroups,
      );
      const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(newDealFileGroupId);

      newDealTemplateInitialData.dealFileGroups = [
        ...newDealTemplateInitialData.dealFileGroups,
        {
          id: newDealFileGroupId,
          name: null,
          files: [],
        },
      ];

      changeForm(`initialData.dealFileGroups[${formKeyDealFileGroup}]`, {
        id: newDealFileGroupId,
        name: null,
        files: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealFileGroupDelete: Function = useCallback((dealFileGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealFileGroups) {
      const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);

      newDealTemplateInitialData.dealFileGroups = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealFileGroups, dealFileGroupId,
      );
      newDealTemplateInitialData.dealFileGroups = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealFileGroups,
      );

      changeForm(`initialData.dealFileGroups[${formKeyDealFileGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealValuationFileGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealValuationFileGroups) {
      const newDealValuationFileGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealValuationFileGroups,
      );
      const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(newDealValuationFileGroupId);

      newDealTemplateInitialData.dealValuationFileGroups = [
        ...newDealTemplateInitialData.dealValuationFileGroups,
        {
          id: newDealValuationFileGroupId,
          name: null,
          files: [],
        },
      ];

      changeForm(`initialData.dealValuationFileGroups[${formKeyDealValuationFileGroup}]`, {
        id: newDealValuationFileGroupId,
        name: null,
        files: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealValuationFileGroupDelete: Function = useCallback((dealValuationFileGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealValuationFileGroups) {
      const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(dealValuationFileGroupId);

      newDealTemplateInitialData.dealValuationFileGroups = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealValuationFileGroups, dealValuationFileGroupId,
      );
      newDealTemplateInitialData.dealValuationFileGroups = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealValuationFileGroups,
      );

      changeForm(`initialData.dealValuationFileGroups[${formKeyDealValuationFileGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealTaskGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTaskGroups) {
      const newDealTaskGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealTaskGroups,
      );
      const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(newDealTaskGroupId);

      newDealTemplateInitialData.dealTaskGroups = [
        ...newDealTemplateInitialData.dealTaskGroups,
        {
          id: newDealTaskGroupId,
          name: null,
          tasks: [],
        },
      ];

      changeForm(`initialData.dealTaskGroups[${formKeyDealTaskGroup}]`, {
        id: newDealTaskGroupId,
        name: null,
        tasks: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealTaskGroupDelete: Function = useCallback((dealTaskGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTaskGroups) {
      const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);

      newDealTemplateInitialData.dealTaskGroups = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealTaskGroups, dealTaskGroupId,
      );
      newDealTemplateInitialData.dealTaskGroups = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealTaskGroups,
      );

      changeForm(`initialData.dealTaskGroups[${formKeyDealTaskGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealTaskAdd: Function = useCallback((dealTaskGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTaskGroups) {
      const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);

      const dealTaskGroupIndex: number = StructureService.getListItemIndexById(
        newDealTemplateInitialData.dealTaskGroups, dealTaskGroupId,
      );

      if (dealTaskGroupIndex || dealTaskGroupIndex === 0) {
        const newDealTaskId: number = StructureService.getNewItemId(
          [], newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks,
        );
        const formKeyDealTask: string = FormService.getIndexedFormFieldKey(newDealTaskId);

        newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks = [
          ...newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks,
          {
            id: newDealTaskId,
            name: null,
            subTasks: [],
          },
        ];

        changeForm(`initialData.dealTaskGroups[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`, {
          id: newDealTaskId,
          name: null,
          subTasks: {},
        });

        setDealTemplateInitialData(newDealTemplateInitialData);

        setIsDealTemplateChanged(true);
      }
    }
  }, [dealTemplateInitialData]);

  const onDealTaskDelete: Function = useCallback((dealTaskGroupId: number, dealTaskId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTaskGroups) {
      const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);

      const dealTaskGroupIndex: number = StructureService.getListItemIndexById(
        newDealTemplateInitialData.dealTaskGroups, dealTaskGroupId,
      );

      if (dealTaskGroupIndex || dealTaskGroupIndex === 0) {
        const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

        newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks = StructureService.deleteListItemById(
          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks, dealTaskId,
        );
        newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks = StructureService.resetObjectListIds(
          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks,
        );

        changeForm(`initialData.dealTaskGroups[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`, null);

        setDealTemplateInitialData(newDealTemplateInitialData);

        setIsDealTemplateChanged(true);
      }
    }
  }, [dealTemplateInitialData]);

  const onDealSubTaskAdd: Function = useCallback((dealTaskGroupId: number, dealTaskId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTaskGroups) {
      const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);

      const dealTaskGroupIndex: number = StructureService.getListItemIndexById(
        newDealTemplateInitialData.dealTaskGroups, dealTaskGroupId,
      );

      if (dealTaskGroupIndex || dealTaskGroupIndex === 0) {
        const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

        const dealTaskIndex: number = StructureService.getListItemIndexById(
          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks, dealTaskId,
        );

        if (dealTaskIndex || dealTaskIndex === 0) {
          const newDealSubTaskId: number = StructureService.getNewItemId(
            [],
            (newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks || []),
          );
          const formKeyDealSubTask: string = FormService.getIndexedFormFieldKey(newDealSubTaskId);

          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks = [
            ...(newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks || []),
            {
              id: newDealSubTaskId,
              name: null,
            },
          ];

          changeForm(
            `initialData.dealTaskGroups[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`
            + `.subTasks[${formKeyDealSubTask}]`,
            {
              id: newDealSubTaskId,
              name: null,
            },
          );

          setDealTemplateInitialData(newDealTemplateInitialData);

          setIsDealTemplateChanged(true);
        }
      }
    }
  }, [dealTemplateInitialData]);

  const onDealSubTaskDelete: Function = useCallback((
    dealTaskGroupId: number, dealTaskId: number, dealSubTaskId: number,
  ): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTaskGroups) {
      const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);

      const dealTaskGroupIndex: number = StructureService.getListItemIndexById(
        newDealTemplateInitialData.dealTaskGroups, dealTaskGroupId,
      );

      if ((dealTaskGroupIndex || dealTaskGroupIndex === 0)
        && newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks) {
        const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

        const dealTaskIndex: number = StructureService.getListItemIndexById(
          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks, dealTaskId,
        );

        if ((dealTaskIndex || dealTaskIndex === 0)
          && newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks) {
          const formKeyDealSubTask: string = FormService.getIndexedFormFieldKey(dealSubTaskId);

          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks = (
            StructureService.deleteListItemById(
              newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks,
              dealSubTaskId,
            )
          );
          newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks = (
            StructureService.resetObjectListIds(
              newDealTemplateInitialData.dealTaskGroups[dealTaskGroupIndex].tasks[dealTaskIndex].subTasks,
            )
          );

          changeForm(`initialData.dealTaskGroups[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`
            + `.subTasks[${formKeyDealSubTask}]`, null);

          setDealTemplateInitialData(newDealTemplateInitialData);

          setIsDealTemplateChanged(true);
        }
      }
    }
  }, [dealTemplateInitialData]);

  const onDealParticipantGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealParticipantGroups) {
      const newDealParticipantGroupId: number = StructureService.getNewItemId(
        [], newDealTemplateInitialData.dealParticipantGroups,
      );
      const formKeyDealParticipantGroup: string = FormService.getIndexedFormFieldKey(newDealParticipantGroupId);

      newDealTemplateInitialData.dealParticipantGroups = [
        ...newDealTemplateInitialData.dealParticipantGroups,
        {
          id: newDealParticipantGroupId,
          name: null,
        },
      ];

      changeForm(`initialData.dealParticipantGroups[${formKeyDealParticipantGroup}]`, {
        id: newDealParticipantGroupId,
        name: null,
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealParticipantGroupDelete: Function = useCallback((dealParticipantGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealParticipantGroups) {
      const formKeyDealParticipantGroup: string = FormService.getIndexedFormFieldKey(dealParticipantGroupId);

      newDealTemplateInitialData.dealParticipantGroups = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealParticipantGroups, dealParticipantGroupId,
      );
      newDealTemplateInitialData.dealParticipantGroups = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealParticipantGroups,
      );

      changeForm(`initialData.dealParticipantGroups[${formKeyDealParticipantGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealTermGroupAdd: Function = useCallback((): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTermGroups) {
      const newDealTermGroupId: number = StructureService.getNewItemId([], newDealTemplateInitialData.dealTermGroups);
      const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(newDealTermGroupId);

      newDealTemplateInitialData.dealTermGroups = [
        ...newDealTemplateInitialData.dealTermGroups,
        {
          id: newDealTermGroupId,
          name: null,
          terms: [],
        },
      ];

      changeForm(`initialData.dealTermGroups[${formKeyDealTermGroup}]`, {
        id: newDealTermGroupId,
        name: null,
        terms: {},
      });

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealTermGroupDelete: Function = useCallback((dealTermGroupId: number): void => {
    const newDealTemplateInitialData: DealTemplateInitialDataInput = dealTemplateInitialData
      ? { ...dealTemplateInitialData as DealTemplateInitialDataInput } : {};

    if (newDealTemplateInitialData.dealTermGroups) {
      const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroupId);

      newDealTemplateInitialData.dealTermGroups = StructureService.deleteListItemById(
        newDealTemplateInitialData.dealTermGroups, dealTermGroupId,
      );
      newDealTemplateInitialData.dealTermGroups = StructureService.resetObjectListIds(
        newDealTemplateInitialData.dealTermGroups,
      );

      changeForm(`initialData.dealTermGroups[${formKeyDealTermGroup}]`, null);

      setDealTemplateInitialData(newDealTemplateInitialData);

      setIsDealTemplateChanged(true);
    }
  }, [dealTemplateInitialData]);

  const onDealTemplateFieldVisibilityChange: Function = useCallback((
    isVisible: boolean, dealTemplateSectionName: string,
  ): void => {
    changeForm(`hiddenFields[${dealTemplateSectionName}].isVisible`, isVisible);

    setIsDealTemplateChanged(true);
  }, []);

  const onDealTemplateCustomSectionAdd: Function = useCallback((): void => {
    const newDealTemplateCustomSectionId: number = StructureService.getNewItemId(
      [], dealTemplateCustomSections,
    );

    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(
      newDealTemplateCustomSectionId,
    ) as string;

    changeForm(`customSections[${formKeyDealTemplateSection}]`, {
      id: newDealTemplateCustomSectionId,
      name: null,
      fields: {},
    });

    setDealTemplateCustomSections([
      ...dealTemplateCustomSections,
      {
        id: newDealTemplateCustomSectionId,
        name: null,
        fields: [],
      },
    ]);

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomSections]);

  const onDealTemplateCustomSectionDelete: Function = useCallback((dealTemplateSectionId: number): void => {
    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(dealTemplateSectionId) as string;

    changeForm(`customSections[${formKeyDealTemplateSection}]`, null);

    const newDealTemplateCustomSectionNewItems: DealTemplateSection[] = [...dealTemplateCustomSections];
    const newDealTemplateCustomSectionIndex: number = newDealTemplateCustomSectionNewItems.findIndex((
      dealTemplateSectionItem: DealTemplateSection,
    ): boolean => (
      dealTemplateSectionItem.id === dealTemplateSectionId
    ));
    if (newDealTemplateCustomSectionIndex !== FormatService.INDEX_NOT_FOUND) {
      delete newDealTemplateCustomSectionNewItems[newDealTemplateCustomSectionIndex];
    }
    setDealTemplateCustomSections(newDealTemplateCustomSectionNewItems
      .filter((dealTemplateSectionItem: DealTemplateSection): boolean => !!dealTemplateSectionItem));

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomSections]);

  const onDealTemplateCustomSectionFieldAdd: Function = useCallback((dealTemplateSectionId: number): void => {
    const dealTemplateCustomSection: DealTemplateSection | null = dealTemplateCustomSections
      ? dealTemplateCustomSections.find((dealTemplateSectionItem: DealTemplateSection): boolean => (
      dealTemplateSectionItem.id === dealTemplateSectionId
    )) || null : null;

    const newDealTemplateCustomSectionFieldId: number = StructureService.getNewItemId(
      [], (dealTemplateCustomSection && dealTemplateCustomSection.fields) || [],
    );

    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(dealTemplateSectionId) as string;
    const formKeyDealTemplateSectionField: string = FormService.getIndexedFormFieldKey(
      newDealTemplateCustomSectionFieldId,
    ) as string;

    changeForm(`customSections[${formKeyDealTemplateSection}].fields[${formKeyDealTemplateSectionField}]`, {
      id: newDealTemplateCustomSectionFieldId,
      name: null,
      type: FIELD_TYPE_TEXT,
    });

    const newDealTemplateCustomSectionNewItems: DealTemplateSection[] = [...dealTemplateCustomSections];
    const newDealTemplateCustomSectionIndex: number = newDealTemplateCustomSectionNewItems.findIndex((
      dealTemplateSectionItem: DealTemplateSection,
    ): boolean => (
      dealTemplateSectionItem.id === dealTemplateSectionId
    ));
    if (newDealTemplateCustomSectionIndex !== FormatService.INDEX_NOT_FOUND) {
      newDealTemplateCustomSectionNewItems[newDealTemplateCustomSectionIndex].fields.push({
        id: newDealTemplateCustomSectionFieldId,
        name: null,
        type: FIELD_TYPE_TEXT,
      });
    }
    setDealTemplateCustomSections(newDealTemplateCustomSectionNewItems);

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomSections]);

  const onDealTemplateCustomSectionFieldTypeChange: Function = useCallback((
    dealTemplateSectionId: number, dealTemplateSectionFieldId: number, dealTemplateSectionFieldType: string,
  ): void => {
    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(dealTemplateSectionId) as string;
    const formKeyDealTemplateSectionField: string = FormService.getIndexedFormFieldKey(
      dealTemplateSectionFieldId,
    ) as string;

    if (dealTemplateSectionFieldType !== FIELD_TYPE_DROPDOWN) {
      changeForm(
        `customSections[${formKeyDealTemplateSection}].fields[${formKeyDealTemplateSectionField}].listType`,
        null,
      );
    }
  }, [dealTemplateCustomSections]);

  const onDealTemplateCustomSectionFieldDelete: Function = useCallback((
    dealTemplateSectionId: number, dealTemplateSectionFieldId: number,
  ): void => {
    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(dealTemplateSectionId) as string;
    const formKeyDealTemplateSectionField: string = FormService.getIndexedFormFieldKey(
      dealTemplateSectionFieldId,
    ) as string;

    changeForm(`customSections[${formKeyDealTemplateSection}].fields[${formKeyDealTemplateSectionField}]`, null);

    const newDealTemplateCustomSectionNewItems: DealTemplateSection[] = [...dealTemplateCustomSections];
    const newDealTemplateCustomSectionIndex: number = newDealTemplateCustomSectionNewItems.findIndex((
      dealTemplateSectionItem: DealTemplateSection,
    ): boolean => (
      dealTemplateSectionItem.id === dealTemplateSectionId
    ));
    if (newDealTemplateCustomSectionIndex !== FormatService.INDEX_NOT_FOUND) {
      const newDealTemplateCustomSection: DealTemplateSection = (
        newDealTemplateCustomSectionNewItems[newDealTemplateCustomSectionIndex]
      );
      const newDealTemplateCustomSectionFieldIndex: number = newDealTemplateCustomSection.fields.findIndex((
        dealTemplateSectionFieldItem: DealTemplateSectionField,
      ): boolean => (
        dealTemplateSectionFieldItem.id === dealTemplateSectionFieldId
      ));

      if (newDealTemplateCustomSectionFieldIndex !== FormatService.INDEX_NOT_FOUND) {
        delete newDealTemplateCustomSectionNewItems[newDealTemplateCustomSectionIndex]
          .fields[newDealTemplateCustomSectionFieldIndex];

        newDealTemplateCustomSectionNewItems[newDealTemplateCustomSectionIndex].fields = (
          newDealTemplateCustomSectionNewItems[newDealTemplateCustomSectionIndex].fields
            .filter((dealTemplateSectionFieldItem: DealTemplateSectionField): boolean => !!dealTemplateSectionFieldItem)
        );
      }
    }
    setDealTemplateCustomSections(newDealTemplateCustomSectionNewItems);

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomSections]);

  const onDealTemplateCustomListTypeAdd: Function = useCallback((): void => {
    const newDealTemplateCustomListTypeId: number = StructureService.getNewItemId(
      [], dealTemplateCustomListTypes,
    );

    const formKeyDealTemplateCustomListType: string = FormService.getIndexedFormFieldKey(
      newDealTemplateCustomListTypeId,
    ) as string;

    changeForm(`customListTypes[${formKeyDealTemplateCustomListType}]`, {
      id: newDealTemplateCustomListTypeId,
      name: null,
      title: null,
      options: {},
    });

    setDealTemplateCustomListTypes([
      ...dealTemplateCustomListTypes,
      {
        id: newDealTemplateCustomListTypeId,
        name: null,
        title: null,
        options: [],
      },
    ]);

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomListTypes]);

  const onDealTemplateCustomListTypeDelete: Function = useCallback((fieldListTypeId: number): void => {
    const formKeyDealTemplateCustomListType: string = FormService.getIndexedFormFieldKey(fieldListTypeId) as string;

    changeForm(`customListTypes[${formKeyDealTemplateCustomListType}]`, null);

    const newDealTemplateCustomListTypeNewItems: FieldListType[] = [...dealTemplateCustomListTypes];
    const newDealTemplateCustomSectionIndex: number = newDealTemplateCustomListTypeNewItems.findIndex((
      fieldListTypeItem: FieldListType,
    ): boolean => (
      fieldListTypeItem.id === fieldListTypeId
    ));
    if (newDealTemplateCustomSectionIndex !== FormatService.INDEX_NOT_FOUND) {
      delete newDealTemplateCustomListTypeNewItems[newDealTemplateCustomSectionIndex];
    }
    setDealTemplateCustomListTypes(newDealTemplateCustomListTypeNewItems
      .filter((fieldListTypeItem: FieldListType): boolean => !!fieldListTypeItem));

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomListTypes]);

  const onDealTemplateCustomListTypeOptionAdd: Function = useCallback((fieldListTypeId: number): void => {
    const dealTemplateCustomListType: FieldListType | null = dealTemplateCustomListTypes
      ? dealTemplateCustomListTypes.find((fieldListTypeItem: FieldListType): boolean => (
      fieldListTypeItem.id === fieldListTypeId
    )) || null : null;

    const newDealTemplateCustomListTypeOptionId: number = StructureService.getNewItemId(
      [], (dealTemplateCustomListType && dealTemplateCustomListType.options) || [],
    );

    const formKeyDealTemplateCustomListType: string = FormService.getIndexedFormFieldKey(fieldListTypeId) as string;
    const formKeyDealTemplateCustomListTypeOption: string = FormService.getIndexedFormFieldKey(
      newDealTemplateCustomListTypeOptionId,
    ) as string;

    changeForm(`customListTypes[${formKeyDealTemplateCustomListType}].options`
      + `[${formKeyDealTemplateCustomListTypeOption}]`, {
      id: newDealTemplateCustomListTypeOptionId,
      name: null,
      title: null,
    });

    const newDealTemplateCustomListTypeNewItems: FieldListType[] = [...dealTemplateCustomListTypes];
    const newDealTemplateCustomListTypeIndex: number = newDealTemplateCustomListTypeNewItems.findIndex((
      fieldListTypeItem: FieldListType,
    ): boolean => (
      fieldListTypeItem.id === fieldListTypeId
    ));
    if (newDealTemplateCustomListTypeIndex !== FormatService.INDEX_NOT_FOUND) {
      if (!newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex].options) {
        newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex].options = [];
      }

      newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex].options.push({
        id: newDealTemplateCustomListTypeOptionId,
        name: null,
        title: null,
      });
    }
    setDealTemplateCustomListTypes(newDealTemplateCustomListTypeNewItems);

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomListTypes]);

  const onDealTemplateCustomListTypeOptionDelete: Function = useCallback((
    fieldListTypeId: number, fieldListTypeOptionId: number,
  ): void => {
    const formKeyDealTemplateCustomFieldType: string = FormService.getIndexedFormFieldKey(fieldListTypeId) as string;
    const formKeyDealTemplateCustomFieldTypeOption: string = FormService.getIndexedFormFieldKey(
      fieldListTypeOptionId,
    ) as string;

    changeForm(
      `customListTypes[${formKeyDealTemplateCustomFieldType}].options[${formKeyDealTemplateCustomFieldTypeOption}]`,
      null,
    );

    const newDealTemplateCustomListTypeNewItems: FieldListType[] = [...dealTemplateCustomListTypes];
    const newDealTemplateCustomListTypeIndex: number = newDealTemplateCustomListTypeNewItems.findIndex((
      fieldListTypeItem: FieldListType,
    ): boolean => (
      fieldListTypeItem.id === fieldListTypeId
    ));
    if (newDealTemplateCustomListTypeIndex !== FormatService.INDEX_NOT_FOUND) {
      const newDealTemplateCustomListType: FieldListType = (
        newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex]
      );

      if (newDealTemplateCustomListType.options) {
        const newDealTemplateCustomListTypeOptionIndex: number = newDealTemplateCustomListType.options.findIndex((
          fieldListTypeOptionItem: FieldListTypeOption,
        ): boolean => (
          fieldListTypeOptionItem.id === fieldListTypeOptionId
        ));

        if (newDealTemplateCustomListTypeOptionIndex !== FormatService.INDEX_NOT_FOUND
          && newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex].options) {
          delete newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex]
            .options[newDealTemplateCustomListTypeOptionIndex];

          newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex].options = (
            newDealTemplateCustomListTypeNewItems[newDealTemplateCustomListTypeIndex].options
              .filter((fieldListTypeOptionItem: FieldListTypeOption): boolean => !!fieldListTypeOptionItem)
          );
        }
      }
    }
    setDealTemplateCustomListTypes(newDealTemplateCustomListTypeNewItems);

    setIsDealTemplateChanged(true);
  }, [dealTemplateCustomListTypes]);

  const onCheckIfDealTemplateHiddenFieldVisible: Function = useCallback((
    dealTemplateHiddenFieldName: string,
  ): boolean => (
    dealTemplateHiddenFields && dealTemplateHiddenFields.length
      ? !dealTemplateHiddenFields.includes(dealTemplateHiddenFieldName) : true
  ), [dealTemplateHiddenFields]);

  const onCheckIfDealTemplateSectionFieldDropdown: Function = useCallback((
    dealTemplateSectionId: number, dealTemplateSectionFieldId: number,
  ): boolean => {
    const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(dealTemplateSectionId) as string;
    const formKeyDealTemplateSectionField: string = FormService.getIndexedFormFieldKey(
      dealTemplateSectionFieldId,
    ) as string;

    const dealTemplateSectionField: DealTemplateSectionField = formValues.customSections
      && formValues.customSections[formKeyDealTemplateSection]
      && formValues.customSections[formKeyDealTemplateSection].fields
      && formValues.customSections[formKeyDealTemplateSection].fields[formKeyDealTemplateSectionField];

    return !!dealTemplateSectionField && dealTemplateSectionField.type === FIELD_TYPE_DROPDOWN;
  }, [formValues]);

  const onCheckIfDealTemplateSectionFieldUsed: Function = useCallback((
    dealTemplateSectionName: string, dealTemplateSectionFieldName: string,
  ): boolean => {
    return !!REPORT_DEAL_CUSTOM_SECTION_FIELD_MAP[REPORT_TYPE_DEAL_BUILD_TO_SUIT_PIPELINE_SUMMARY].find(
      ([reportDealCustomSectionName, reportDealCustomSectionFieldName]: [string, string]): boolean => (
        reportDealCustomSectionName === dealTemplateSectionName
        && reportDealCustomSectionFieldName === dealTemplateSectionFieldName
      ),
    );
  }, []);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      component: (
        <DealTemplateSummary
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_SUMMARY || !queryTab}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateData={dealTemplateData}
          dealTemplateInitialData={dealTemplateInitialData}
          dealTemplateCustomSections={dealTemplateCustomSections}
          dealTemplateTypeList={dealTemplateTypeList}
          portfolioList={portfolioList}
          measureUnitList={measureUnitList}
          fieldTypeList={fieldTypeList}
          fieldListTypeList={commonFieldListTypeList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onEditModeButtonClick={onEditModeButtonClick}
          onDealAssetUseBreakdownHeaderAdd={onDealAssetUseBreakdownHeaderAdd}
          onDealAssetUseBreakdownHeaderDelete={onDealAssetUseBreakdownHeaderDelete}
          onDealInvestmentReturnsItemAdd={onDealInvestmentReturnsItemAdd}
          onDealInvestmentReturnsItemDelete={onDealInvestmentReturnsItemDelete}
          onDealContactsGroupAdd={onDealContactsGroupAdd}
          onDealContactsGroupDelete={onDealContactsGroupDelete}
          onDealCapitalizationSourceGroupAdd={onDealCapitalizationSourceGroupAdd}
          onDealCapitalizationSourceGroupDelete={onDealCapitalizationSourceGroupDelete}
          onDealFileGroupAdd={onDealFileGroupAdd}
          onDealFileGroupDelete={onDealFileGroupDelete}
          onDealTemplateFieldVisibilityChange={onDealTemplateFieldVisibilityChange}
          onDealTemplateCustomSectionAdd={onDealTemplateCustomSectionAdd}
          onDealTemplateCustomSectionFieldAdd={onDealTemplateCustomSectionFieldAdd}
          onDealTemplateCustomSectionDelete={onDealTemplateCustomSectionDelete}
          onDealTemplateCustomSectionFieldDelete={onDealTemplateCustomSectionFieldDelete}
          onDealTemplateCustomSectionFieldTypeChange={onDealTemplateCustomSectionFieldTypeChange}
          onCheckIfDealTemplateHiddenFieldVisible={onCheckIfDealTemplateHiddenFieldVisible}
          onCheckIfDealTemplateSectionFieldDropdown={onCheckIfDealTemplateSectionFieldDropdown}
          onCheckIfDealTemplateSectionFieldUsed={onCheckIfDealTemplateSectionFieldUsed}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_CAPITALIZATION,
      title: 'Capitalization',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateCapitalization
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_CAPITALIZATION}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateInitialData={dealTemplateInitialData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealCapitalizationSourceGroupAdd={onDealCapitalizationSourceGroupAdd}
          onDealCapitalizationSourceGroupDelete={onDealCapitalizationSourceGroupDelete}
          onDealCapitalizationUseGroupAdd={onDealCapitalizationUseGroupAdd}
          onDealCapitalizationUseGroupDelete={onDealCapitalizationUseGroupDelete}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_PRO_FORMA,
      title: 'Pro Forma',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateProForma
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_PRO_FORMA}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateInitialData={dealTemplateInitialData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealValuationFileGroupAdd={onDealValuationFileGroupAdd}
          onDealValuationFileGroupDelete={onDealValuationFileGroupDelete}
        />
      ),
    },
    {
      id: 4,
      name: TAB_NAME_DUE_DILIGENCE,
      title: 'Due Diligence',
      component: (
        <DealTemplateDueDiligence
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_DUE_DILIGENCE}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateInitialData={dealTemplateInitialData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealTaskGroupAdd={onDealTaskGroupAdd}
          onDealTaskGroupDelete={onDealTaskGroupDelete}
          onDealTaskAdd={onDealTaskAdd}
          onDealTaskDelete={onDealTaskDelete}
          onDealSubTaskAdd={onDealSubTaskAdd}
          onDealSubTaskDelete={onDealSubTaskDelete}
        />
      ),
    },
    {
      id: 5,
      name: TAB_NAME_DEAL_FILES,
      title: 'Deal Files',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateDealFiles
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_DEAL_FILES}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateInitialData={dealTemplateInitialData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealFileGroupAdd={onDealFileGroupAdd}
          onDealFileGroupDelete={onDealFileGroupDelete}
        />
      ),
    },
    {
      id: 6,
      name: TAB_NAME_DEAL_TERMS,
      title: 'Deal Terms',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateDealTerms
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_DEAL_TERMS}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateInitialData={dealTemplateInitialData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealTermGroupAdd={onDealTermGroupAdd}
          onDealTermGroupDelete={onDealTermGroupDelete}
        />
      ),
    },
    {
      id: 7,
      name: TAB_NAME_SCHEDULE,
      title: 'Schedule',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateSchedule
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_SCHEDULE}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 8,
      name: TAB_NAME_PARTICIPANTS,
      title: 'Participants',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateParticipants
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_PARTICIPANTS}
          isRightDrawerOpen={isRightDrawerOpen}
          dealTemplateInitialData={dealTemplateInitialData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealParticipantGroupAdd={onDealParticipantGroupAdd}
          onDealParticipantGroupDelete={onDealParticipantGroupDelete}
        />
      ),
    },
    {
      id: 9,
      name: TAB_NAME_DEAL_ACTIVITY,
      title: 'Deal Activity',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateDealActivity
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_DEAL_ACTIVITY}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 10,
      name: TAB_NAME_DEAL_LIST_TYPES,
      title: 'Deal List Types',
      isHidden: dealTemplateType !== DEAL_TEMPLATE_TYPE_NEW_DEAL_TEMPLATE,
      component: (
        <DealTemplateDealListTypes
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isActive={queryTab === TAB_NAME_DEAL_LIST_TYPES}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          dealTemplateCustomListTypes={dealTemplateCustomListTypes}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealTemplateCustomListTypeAdd={onDealTemplateCustomListTypeAdd}
          onDealTemplateCustomListTypeDelete={onDealTemplateCustomListTypeDelete}
          onDealTemplateCustomListTypeOptionAdd={onDealTemplateCustomListTypeOptionAdd}
          onDealTemplateCustomListTypeOptionDelete={onDealTemplateCustomListTypeOptionDelete}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: `${ROUTE_DEAL_TEMPLATE}/${dealTemplateId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [
    locationQueryParams, tabs, dealTemplateId, dealTemplateInitialDataDefault, dealTemplateHiddenFieldsDefault,
    dealTemplateCustomSectionsDefault,
  ]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): ReactUseEffectCleanUp => {
    if (dealTemplateId) {
      dispatch(getDealTemplateData({
        dealTemplateId,
      }));
    }

    return (): void => {
      dispatch(resetDealTemplateData());
    };
  }, [dealTemplateId]);

  useEffect((): void => {
    if (isFormProcessed) {
      onCancelForm();

      setIsFormProcessed(false);
    }
  }, [isFormProcessed]);

  useEffect((): void => {
    if (isDataLoaded) {
      resetDynamicData();
    }
  }, [isDataLoaded]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_DEAL_TEMPLATE}/${dealTemplateId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!dealTemplateId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  return (
    <CheckAccess permissions={dealTemplateEditPermissions}>
      <Form id={FORM_ID_SETTINGS_DEAL_TEMPLATE} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isDisabled={isEditMode}
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_DEAL_TEMPLATES}
            currentUser={currentUser}
            onBeforeLocationChange={onBeforeLocationChange}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && (
              <Fragment>
                <Controls onCancelForm={onCancelForm} />
                <LimitedPageContainer isActive>
                  <PageTabs
                    isCompressed={isRightDrawerOpen}
                    isDisabled={isFormChanged}
                    tabs={tabs}
                    currentTab={queryTab}
                    onTabChange={onTabChange}
                    onBeforeTabChange={onBeforeTabChange}
                  />
                </LimitedPageContainer>
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onConfirm={onLeaveConfirm}
          onCloseModal={closeLeaveConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
    settingsDealTemplate,
  } = reducers;

  const dealTemplateData: DealTemplate = settingsDealTemplate && settingsDealTemplate.dealTemplateData
    && settingsDealTemplate.dealTemplateData.dealTemplate;

  if (dealTemplateData) {
    initialValues.id = dealTemplateData.id;
    initialValues.title = dealTemplateData.title;
    initialValues.type = dealTemplateData.type;
    initialValues.description = dealTemplateData.description ? dealTemplateData.description : null;

    if (dealTemplateData.initialData) {
      initialValues.initialData = {};

      if (dealTemplateData.initialData.asset) {
        initialValues.initialData.asset = {};

        if (dealTemplateData.initialData.asset.portfolio && dealTemplateData.initialData.asset.portfolio.id) {
          initialValues.initialData.asset.portfolioId = dealTemplateData.initialData.asset.portfolio.id;
        }
      }

      const dealBuildingDetailsAssetUseBreakdownHeaders: DealAssetUseBreakdownHeader[] | undefined = (
        dealTemplateData.initialData.dealBuildingDetailsAssetUseBreakdownHeaders
      );

      initialValues.initialData.dealBuildingDetailsAssetUseBreakdownHeaders = {};
      if (dealBuildingDetailsAssetUseBreakdownHeaders && dealBuildingDetailsAssetUseBreakdownHeaders.length) {
        dealBuildingDetailsAssetUseBreakdownHeaders.forEach((
          dealAssetUseBreakdownHeaderItem: DealAssetUseBreakdownHeader,
        ): void => {
          const formKeyDealAssetUseBreakdownHeader: string = FormService.getIndexedFormFieldKey(
            dealAssetUseBreakdownHeaderItem.id,
          );

          initialValues.initialData.dealBuildingDetailsAssetUseBreakdownHeaders[formKeyDealAssetUseBreakdownHeader] = {
            id: dealAssetUseBreakdownHeaderItem.id,
            name: dealAssetUseBreakdownHeaderItem.name,
          };
        });
      }

      const dealContacts: DealContactGroup[] | undefined = dealTemplateData.initialData.dealContacts;

      initialValues.initialData.dealContacts = {};
      if (dealContacts && dealContacts.length) {
        dealContacts.forEach((dealContactsGroupItem: DealContactGroup): void => {
          const formKeyDealContactsGroupItem: string = FormService.getIndexedFormFieldKey(dealContactsGroupItem.id);

          initialValues.initialData.dealContacts[formKeyDealContactsGroupItem] = {
            id: dealContactsGroupItem.id,
            name: dealContactsGroupItem.name,
          };
        });
      }

      const dealInvestmentReturns: DealInvestmentReturnsItem[] | undefined = (
        dealTemplateData.initialData.dealInvestmentReturns
      );

      initialValues.initialData.dealInvestmentReturns = {};
      if (dealInvestmentReturns && dealInvestmentReturns.length) {
        dealInvestmentReturns.forEach((dealInvestmentReturnsItem: DealInvestmentReturnsItem): void => {
          const formKeyDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
            dealInvestmentReturnsItem.id,
          );

          initialValues.initialData.dealInvestmentReturns[formKeyDealInvestmentReturnsItem] = {
            id: dealInvestmentReturnsItem.id,
            name: dealInvestmentReturnsItem.name,
          };
        });
      }

      const dealCapitalization: DealCapitalizationData | undefined = (
        dealTemplateData.initialData.dealCapitalization
      );

      initialValues.initialData.dealCapitalization = {};
      if (dealCapitalization) {
        initialValues.initialData.dealCapitalization.sources = {};
        initialValues.initialData.dealCapitalization.uses = {};

        if (dealCapitalization.sources && dealCapitalization.sources.length) {
          dealCapitalization.sources.forEach((dealCapitalizationGroup: DealCapitalizationGroup): void => {
            const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
              dealCapitalizationGroup.id,
            );

            initialValues.initialData.dealCapitalization.sources[formKeyDealCapitalizationGroup] = {
              id: dealCapitalizationGroup.id,
              name: dealCapitalizationGroup.name,
            };
          });
        }

        if (dealCapitalization.uses && dealCapitalization.uses.length) {
          dealCapitalization.uses.forEach((dealCapitalizationGroup: DealCapitalizationGroup): void => {
            const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
              dealCapitalizationGroup.id,
            );

            initialValues.initialData.dealCapitalization.uses[formKeyDealCapitalizationGroup] = {
              id: dealCapitalizationGroup.id,
              name: dealCapitalizationGroup.name,
            };
          });
        }
      }

      const dealTermGroups: DealTermGroup[] | undefined = (
        dealTemplateData.initialData.dealTermGroups
      );

      initialValues.initialData.dealTermGroups = {};
      if (dealTermGroups && dealTermGroups.length) {
        dealTermGroups.forEach((dealTermGroup: DealTermGroup): void => {
          const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroup.id);

          initialValues.initialData.dealTermGroups[formKeyDealTermGroup] = {
            id: dealTermGroup.id,
            name: dealTermGroup.name,
          };
        });
      }

      const dealValuationFileGroups: DealFileGroup[] | undefined = (
        dealTemplateData.initialData.dealValuationFileGroups
      );

      initialValues.initialData.dealValuationFileGroups = {};
      if (dealValuationFileGroups && dealValuationFileGroups.length) {
        dealValuationFileGroups.forEach((dealFileGroup: DealFileGroup): void => {
          const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroup.id);

          initialValues.initialData.dealValuationFileGroups[formKeyDealFileGroup] = {
            id: dealFileGroup.id,
            name: dealFileGroup.name,
          };
        });
      }

      const dealTaskGroups: DealTaskGroup[] | undefined = (
        dealTemplateData.initialData.dealTaskGroups
      );

      initialValues.initialData.dealTaskGroups = {};
      if (dealTaskGroups && dealTaskGroups.length) {
        dealTaskGroups.forEach((dealTaskGroup: DealTaskGroup): void => {
          const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroup.id);

          initialValues.initialData.dealTaskGroups[formKeyDealTaskGroup] = {
            id: dealTaskGroup.id,
            name: dealTaskGroup.name,
            tasks: {},
          };

          if (dealTaskGroup.tasks && dealTaskGroup.tasks.length) {
            dealTaskGroup.tasks.forEach((dealTask: DealTask): void => {
              const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTask.id);

              initialValues.initialData.dealTaskGroups[formKeyDealTaskGroup].tasks[formKeyDealTask] = {
                id: dealTask.id,
                name: dealTask.name,
                subTasks: {},
              };

              if (dealTask.subTasks && dealTask.subTasks.length) {
                dealTask.subTasks.forEach((dealSubTask: DealTask): void => {
                  const formKeyDealSubTask: string = FormService.getIndexedFormFieldKey(dealSubTask.id);

                  initialValues.initialData.dealTaskGroups[formKeyDealTaskGroup].tasks[formKeyDealTask]
                    .subTasks[formKeyDealSubTask] = {
                    id: dealSubTask.id,
                    name: dealSubTask.name,
                  };
                });
              }
            });
          }
        });
      }

      const dealFileGroups: DealFileGroup[] | undefined = (
        dealTemplateData.initialData.dealFileGroups
      );

      initialValues.initialData.dealFileGroups = {};
      if (dealFileGroups && dealFileGroups.length) {
        dealFileGroups.forEach((dealFileGroup: DealFileGroup): void => {
          const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroup.id);

          initialValues.initialData.dealFileGroups[formKeyDealFileGroup] = {
            id: dealFileGroup.id,
            name: dealFileGroup.name,
          };
        });
      }

      const dealParticipantGroups: DealParticipantGroup[] | undefined = (
        dealTemplateData.initialData.dealParticipantGroups
      );

      initialValues.initialData.dealParticipantGroups = {};
      if (dealParticipantGroups && dealParticipantGroups.length) {
        dealParticipantGroups.forEach((dealParticipantGroup: DealParticipantGroup): void => {
          const formKeyDealParticipantGroup: string = FormService.getIndexedFormFieldKey(dealParticipantGroup.id);

          initialValues.initialData.dealParticipantGroups[formKeyDealParticipantGroup] = {
            id: dealParticipantGroup.id,
            name: dealParticipantGroup.name,
          };
        });
      }
    }

    const hiddenFields: string[] = dealTemplateData.hiddenFields ? [...dealTemplateData.hiddenFields] : [];

    initialValues.hiddenFields = {};
    DEAL_TEMPLATE_KEY_LIST.forEach((dealTemplateSectionName: string): void => {
      initialValues.hiddenFields[dealTemplateSectionName] = {
        isVisible: hiddenFields && hiddenFields.length ? !hiddenFields.includes(dealTemplateSectionName) : true,
      };
    });

    const customSections: DealTemplateSection[] = dealTemplateData.customSections
      ? [...dealTemplateData.customSections] : [];

    initialValues.customSections = {};
    if (customSections && customSections.length) {
      customSections.forEach((dealTemplateSectionItem: DealTemplateSection): void => {
        const formKeyDealTemplateSection: string = FormService.getIndexedFormFieldKey(
          dealTemplateSectionItem.id,
        );

        initialValues.customSections[formKeyDealTemplateSection] = {
          id: dealTemplateSectionItem.id,
          name: dealTemplateSectionItem.name,
          fields: {},
        };

        if (dealTemplateSectionItem.fields && dealTemplateSectionItem.fields.length) {
          dealTemplateSectionItem.fields.forEach((dealTemplateSectionField: DealTemplateSectionField): void => {
            const formKeyDealTemplateSectionField: string = FormService.getIndexedFormFieldKey(
              dealTemplateSectionField.id,
            );
            const dealTemplateSectionFieldData: DealTemplateSectionField = {
              id: dealTemplateSectionField.id,
              name: dealTemplateSectionField.name,
              type: dealTemplateSectionField.type || FIELD_TYPE_TEXT,
            };

            if (dealTemplateSectionField.listType) {
              dealTemplateSectionFieldData.listType = dealTemplateSectionField.listType;
            }

            initialValues.customSections[formKeyDealTemplateSection]
              .fields[formKeyDealTemplateSectionField] = dealTemplateSectionFieldData;
          });
        }
      });
    }

    const customListTypes: FieldListType[] = dealTemplateData.customListTypes
      ? [...dealTemplateData.customListTypes] : [];

    initialValues.customListTypes = {};
    if (customListTypes && customListTypes.length) {
      customListTypes.forEach((fieldListTypeItem: FieldListType): void => {
        const formKeyFieldListTypeItem: string = FormService.getIndexedFormFieldKey(
          fieldListTypeItem.id,
        );

        initialValues.customListTypes[formKeyFieldListTypeItem] = {
          id: fieldListTypeItem.id,
          name: fieldListTypeItem.name,
          title: fieldListTypeItem.title,
          options: {},
        };

        if (fieldListTypeItem.options && fieldListTypeItem.options.length) {
          fieldListTypeItem.options.forEach((dealTemplateListTypeOption: FieldListTypeOption): void => {
            const formKeyDealTemplateCustomListTypeOption: string = FormService.getIndexedFormFieldKey(
              dealTemplateListTypeOption.id,
            );

            initialValues.customListTypes[formKeyFieldListTypeItem].options[formKeyDealTemplateCustomListTypeOption] = {
              id: dealTemplateListTypeOption.id,
              name: dealTemplateListTypeOption.name,
              title: dealTemplateListTypeOption.title,
            };
          });
        }
      });
    }
  }

  return {
    isDataLoaded: !!settingsDealTemplate.dealTemplateData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    dealTemplateData: settingsDealTemplate.dealTemplateData && settingsDealTemplate.dealTemplateData.dealTemplate,
    dealTemplateTypeList: settingsDealTemplate.dealTemplateData
      && settingsDealTemplate.dealTemplateData.dealTemplateTypeList,
    portfolioList: settingsDealTemplate.dealTemplateData
      && settingsDealTemplate.dealTemplateData.portfolioList,
    measureUnitList: settingsDealTemplate.dealTemplateData
      && settingsDealTemplate.dealTemplateData.measureUnitList,
    fieldTypeList: settingsDealTemplate.dealTemplateData
      && settingsDealTemplate.dealTemplateData.fieldTypeList,
    fieldListTypeList: settingsDealTemplate.dealTemplateData
      && settingsDealTemplate.dealTemplateData.fieldListTypeList,
  };
};

const SettingsDealTemplateForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settingsDealTemplate }: Reducers): ValidationErrorsMap => (
    settingsDealTemplate.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settingsDealTemplate }: Reducers): ValidationErrorsMap => (
    settingsDealTemplate.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_DEAL_TEMPLATE}
      component={SettingsDealTemplate}
      validator={SettingsDealTemplateValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsDealTemplateForm);
